import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { LEAD_STAGES, LeadStages } from 'models/Lead';
import {
  AVAILABILITY_STATE,
  BulkAvailabilityState,
} from 'router/subrouters/DataRoom/models/DataRoomBulk';
import {
  getArrayLength,
  getCanShareNode,
  getIsArrayEmpty,
  getIsNodeFile,
  getShareableChildIds,
  includesIgnoreCase,
} from 'utils';
import {
  DraftMessageOrderItem,
  FileOrderItem,
  ORDER_ITEM_TYPE,
  OrderFormItem,
  OrderItem,
} from 'models/WorkflowConfiguration';
import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';

export type StageFilesTree = {
  rootNodes: DataRoomNode[];
  childrenByParent: Record<string, DataRoomNode[]>;
  nodesById: Record<string, DataRoomNode>;
  parentById: Record<string, string>;
  isLoading: boolean;
};

export type FileOrderEntry = {
  type: typeof ORDER_ITEM_TYPE.FILE;
  key: string;
  node: DataRoomNode;
  // Direct parent folder, shown before the size.
  parentName?: string;
  isRemovable: boolean;
};

export type MessageOrderEntry = {
  type: typeof ORDER_ITEM_TYPE.MESSAGE;
  key: string;
  // Index in the orderItems field, which its text input binds to.
  itemIndex: number;
  message: DraftMessageOrderItem;
  // The file it goes out with; none at the end of the order.
  sentWith?: DataRoomNode;
};

export type OrderedStageEntry = FileOrderEntry | MessageOrderEntry;

export type SelectedStageEntry = {
  node: DataRoomNode;
  selectedCount: number;
  totalCount: number;
  size?: number;
  // What unticking it in Files would remove; empty when its checkbox is disabled.
  removableIds: string[];
};

export const getIsNodeDisabled = (node: DataRoomNode) =>
  !getCanShareNode(node) || node.enabledState === AVAILABILITY_STATE.OFF;

// Earlier stages whose ticks lock the node, in stage order.
export const getNodeLockStages = (
  node: DataRoomNode,
  stageByFileId: Record<string, LeadStages>,
) => {
  const ids = getIsNodeFile(node)
    ? [node._id]
    : getShareableChildIds(node) || [];
  const stages = new Set(ids.map((id) => stageByFileId[id]));

  return Object.values(LEAD_STAGES).filter((stage) => stages.has(stage));
};

// File ids a tick on this node adds or removes.
export const getSelectableFileIds = (
  node: DataRoomNode,
  nodesById: Record<string, DataRoomNode>,
) => {
  if (getIsNodeFile(node)) return [node._id];

  return (getShareableChildIds(node) || []).filter(
    (id) => nodesById[id]?.enabledState !== AVAILABILITY_STATE.OFF,
  );
};

export const getNodeSelectionState = (
  node: DataRoomNode,
  checkedIds: Set<string>,
  nodesById: Record<string, DataRoomNode>,
): BulkAvailabilityState => {
  const ids = getSelectableFileIds(node, nodesById);
  const selectedCount = getArrayLength(ids.filter((id) => checkedIds.has(id)));

  if (!selectedCount) return AVAILABILITY_STATE.OFF;

  return selectedCount === getArrayLength(ids)
    ? AVAILABILITY_STATE.ON
    : AVAILABILITY_STATE.MIXED;
};

// Files a folder tick adds, in the order Files shows them; unloaded ones go last.
const getOrderedSelectableFileIds = (
  node: DataRoomNode,
  { nodesById, childrenByParent }: StageFilesTree,
) => {
  const ids = getSelectableFileIds(node, nodesById);

  if (getIsNodeFile(node)) return ids;

  const selectableIds = new Set(ids);
  const loadedIds: string[] = [];

  const collect = (parentId: string) =>
    (childrenByParent[parentId] || []).forEach((child) => {
      if (!getIsNodeFile(child)) collect(child._id);
      else if (selectableIds.has(child._id)) loadedIds.push(child._id);
    });

  collect(node._id);

  const loaded = new Set(loadedIds);

  return [...loadedIds, ...ids.filter((id) => !loaded.has(id))];
};

const setIds = (checkedIds: string[], ids: string[], shouldSelect: boolean) => {
  if (!shouldSelect) {
    const removed = new Set(ids);

    return checkedIds.filter((id) => !removed.has(id));
  }

  const current = new Set(checkedIds);

  // New ticks join the end, so the order built so far stays put.
  return [...checkedIds, ...ids.filter((id) => !current.has(id))];
};

export const toggleNodeSelection = (
  node: DataRoomNode,
  checkedIds: string[],
  tree: StageFilesTree,
) =>
  setIds(
    checkedIds,
    getOrderedSelectableFileIds(node, tree),
    getNodeSelectionState(node, new Set(checkedIds), tree.nodesById) !==
      AVAILABILITY_STATE.ON,
  );

export const removeSelection = (checkedIds: string[], ids: string[]) =>
  setIds(checkedIds, ids, false);

const getRemovableIds = (
  node: DataRoomNode,
  checkedIds: Set<string>,
  nodesById: Record<string, DataRoomNode>,
) =>
  getIsNodeDisabled(node)
    ? []
    : getSelectableFileIds(node, nodesById).filter((id) => checkedIds.has(id));

// One card per top-level node: a file, or a folder holding any selected file.
export const getSelectedStageEntries = (
  { rootNodes, nodesById }: StageFilesTree,
  checkedIds: Set<string>,
) =>
  rootNodes.reduce<SelectedStageEntry[]>((entries, node) => {
    if (getIsNodeFile(node)) {
      if (!checkedIds.has(node._id)) return entries;

      return [
        ...entries,
        {
          node,
          selectedCount: 1,
          totalCount: 1,
          size: node.document?.size,
          removableIds: getRemovableIds(node, checkedIds, nodesById),
        },
      ];
    }

    const shareableIds = getShareableChildIds(node) || [];
    const selectedIds = shareableIds.filter((id) => checkedIds.has(id));

    if (getIsArrayEmpty(selectedIds)) return entries;

    const selectedCount = getArrayLength(selectedIds);
    const totalCount = getArrayLength(shareableIds);

    // A partial folder's size is only known once all its selected files are loaded.
    const size =
      selectedCount === totalCount
        ? node.folderSize
        : selectedIds.every((id) => nodesById[id])
        ? selectedIds.reduce(
            (sum, id) => sum + (nodesById[id].document?.size || 0),
            0,
          )
        : undefined;

    return [
      ...entries,
      {
        node,
        selectedCount,
        totalCount,
        size,
        removableIds: getRemovableIds(node, checkedIds, nodesById),
      },
    ];
  }, []);

export const isFileOrderItem = (item: OrderFormItem): item is FileOrderItem =>
  item.type === ORDER_ITEM_TYPE.FILE;

export const getOrderItemKey = (item: OrderFormItem) =>
  isFileOrderItem(item) ? item.dataRoomNodeId : item.key;

export const getCheckedFileIds = (items: OrderFormItem[] = []) =>
  items.filter(isFileOrderItem).map(({ dataRoomNodeId }) => dataRoomNodeId);

// Messages keep their place; new files go before any closing messages, so those stay last.
export const syncCheckedFiles = (
  items: OrderFormItem[] = [],
  checkedIds: string[] = [],
) => {
  const checked = new Set(checkedIds);
  const current = new Set(getCheckedFileIds(items));

  const kept = items.filter(
    (item) => !isFileOrderItem(item) || checked.has(item.dataRoomNodeId),
  );
  const added = checkedIds
    .filter((id) => !current.has(id))
    .map<FileOrderItem>((dataRoomNodeId) => ({
      type: ORDER_ITEM_TYPE.FILE,
      dataRoomNodeId,
    }));

  let endIndex = getArrayLength(kept);

  while (endIndex && !isFileOrderItem(kept[endIndex - 1])) endIndex--;

  return [...kept.slice(0, endIndex), ...added, ...kept.slice(endIndex)];
};

export const createMessageItem = (): DraftMessageOrderItem => ({
  type: ORDER_ITEM_TYPE.MESSAGE,
  key: uniqueId('message-'),
  text: '',
});

// No key, or one not in the order, points past the end.
const getInsertIndex = (items: OrderFormItem[], beforeKey?: string) => {
  const index = beforeKey
    ? items.findIndex((item) => getOrderItemKey(item) === beforeKey)
    : -1;

  return index < 0 ? getArrayLength(items) : index;
};

export const insertOrderItem = (
  items: OrderFormItem[],
  item: OrderFormItem,
  beforeKey?: string,
) => {
  const index = getInsertIndex(items, beforeKey);

  return [...items.slice(0, index), item, ...items.slice(index)];
};

// A gap between two files holds one message: the one above the spot, else below.
export const getGapMessageKey = (items: OrderFormItem[], beforeKey?: string) => {
  const index = getInsertIndex(items, beforeKey);
  const message = [items[index - 1], items[index]].find(
    (item) => item && !isFileOrderItem(item),
  );

  return message && getOrderItemKey(message);
};

// The step's own saved sequence, minus files unticked since. BE puts every file ticked
// here in it; checkedIds also holds files reached in earlier stages, so never add from it.
export const toOrderFormItems = (
  orderedItems: OrderItem[] = [],
  checkedIds: string[] = [],
) => {
  const checked = new Set(checkedIds);

  return orderedItems.reduce<OrderFormItem[]>((items, item) => {
    if (item.type === ORDER_ITEM_TYPE.MESSAGE)
      return [...items, { ...item, key: item._id || uniqueId('message-') }];

    return checked.has(item.dataRoomNodeId) ? [...items, item] : items;
  }, []);
};

// A message step with no text is not saved.
export const toOrderPayload = (items: OrderFormItem[] = []) =>
  items.reduce<OrderItem[]>((payload, item) => {
    if (isFileOrderItem(item)) return [...payload, item];

    const { type, _id } = item;
    const text = item.text?.trim();

    if (!text) return payload;

    return [...payload, { type, text, ...(_id && { _id }) }];
  }, []);

// "Everything at once" has no order, so there only the set of files counts.
export const getIsSameSelection = (
  items: OrderFormItem[] = [],
  initialItems: OrderFormItem[] = [],
  isOrdered: boolean,
) =>
  isOrdered
    ? isEqual(toOrderPayload(items), toOrderPayload(initialItems))
    : isEqual(
        getCheckedFileIds(items).sort(),
        getCheckedFileIds(initialItems).sort(),
      );

// Files not loaded yet are left out until they are.
export const getOrderedStageEntries = (
  { nodesById, parentById }: StageFilesTree,
  items: OrderFormItem[] = [],
) => {
  const entries = items.reduce<OrderedStageEntry[]>(
    (entries, item, itemIndex) => {
      if (!isFileOrderItem(item)) {
        return [
          ...entries,
          { type: item.type, key: item.key, itemIndex, message: item },
        ];
      }

      const node = nodesById[item.dataRoomNodeId];

      if (!node) return entries;

      return [
        ...entries,
        {
          type: item.type,
          key: node._id,
          node,
          parentName: nodesById[parentById[node._id]]?.name,
          isRemovable: !getIsNodeDisabled(node),
        },
      ];
    },
    [],
  );

  // Walks up from the end, so each message picks up the first file below it.
  let nextFile: DataRoomNode | undefined;

  for (let index = getArrayLength(entries) - 1; index >= 0; index--) {
    const entry = entries[index];

    if (entry.type === ORDER_ITEM_TYPE.FILE) nextFile = entry.node;
    else entry.sentWith = nextFile;
  }

  return entries;
};

export const getDocumentsAvailabilityChanges = (
  checkedIds: string[] = [],
  initialCheckedIds: string[] = [],
) => [
  ...checkedIds
    .filter((id) => !initialCheckedIds.includes(id))
    .map((dataRoomNodeId) => ({ dataRoomNodeId, checked: true })),
  ...initialCheckedIds
    .filter((id) => !checkedIds.includes(id))
    .map((dataRoomNodeId) => ({ dataRoomNodeId, checked: false })),
];

// Matches plus their ancestors, so a hit deep in the tree stays reachable.
export const getSearchMatches = (
  { nodesById, parentById }: StageFilesTree,
  searchString: string,
) => {
  const matchedIds = new Set<string>();
  const ancestorIds = new Set<string>();

  Object.keys(nodesById).forEach((id) => {
    if (!includesIgnoreCase(nodesById[id].name, searchString)) return;

    matchedIds.add(id);

    for (let parentId = parentById[id]; parentId; ) {
      ancestorIds.add(parentId);
      parentId = parentById[parentId];
    }
  });

  return { matchedIds, ancestorIds };
};
